import React, { useEffect, useState } from 'react';
import { Button, Card, Row } from 'react-bootstrap';
import dayjs from 'dayjs';

import { faBorderAll, faList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { useAuthContext } from 'context/authContext';
import useUpload from 'hooks/useUpload';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddImageModal from './AddImageModal';
import ImageGrid from './ImageGrid';
import ImageList from './ImageList';
import Flex from 'components/common/Flex';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import { useGalleryItems, useCreateGalleryItem } from 'hooks/useBeneficiaries';

const PAGE_SIZE = 2;

function Gellery() {
  const isList = 'list';
  const isGrid = 'grid';
  const [layout, setLayout] = useState('list');
  const [addImageShow, setAddImageShow] = useState(false);
  const { upload, loading: uploadLoading } = useUpload();
  const { organization, selectedBranchId, user } = useAuthContext();
  const { id } = useParams();

  const { galleryItems, refetch: refetchGallery } = useGalleryItems(id || organization.id);
  const { createGalleryItem } = useCreateGalleryItem();

  const onSubmit = async values => {
    try {
      if (values.image && typeof values.image !== 'string') {
        const imageUrl = await upload(values.image);
        if (!imageUrl) {
          toast.error('Failed to upload image. Please try again.');
          return;
        }
        createGalleryItem({
          branch_id: selectedBranchId,
          gallery_image: imageUrl,
          image_location: imageUrl,
          organisation_id: organization.id,
          orphan_id: id,
          user_id: user.id,
          image_description: values.description,
          registered_date: dayjs().format('YYYY-MM-DD')
        }, {
          onSuccess: () => {
            setAddImageShow(false);
            refetchGallery();
          }
        });
      } else {
        toast.error('Please select or take a photo first.');
      }
    } catch (error) {
      toast.error('Something went wrong! Please try again later!');
      return;
    }
  };

  const [globalFilter, setGlobalFilter] = useState('');
  const [pageOffset, setPageOffset] = useState(0);

  const [selectedId, setSelectedId] = useState();
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);

  const handleDeleteImageHide = () => {
    setShowDeleteImageModal(false);
  };

  const handleConfirmDelete = () => {
    // TODO: Implement delete when hook is available
    toast.info('Delete functionality coming soon');
    setShowDeleteImageModal(false);
  };

  // Filter gallery items by search
  const filteredData = (galleryItems || []).filter(item =>
    !globalFilter ||
    item.image_description?.toLowerCase().includes(globalFilter.toLowerCase())
  );

  const start = pageOffset * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const paginatedItems = filteredData.slice(start, end);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);

  const handleNext = () => {
    setPageOffset(pageOffset + 1);
  };

  const handlePrev = () => {
    setPageOffset(pageOffset - 1);
  };

  useEffect(() => {
    setPageOffset(0);
  }, [globalFilter]);

  return (
    <>
      <ConfirmationModal
        show={showDeleteImageModal}
        onHide={handleDeleteImageHide}
        title={'Delete Image'}
        handleConfirm={handleConfirmDelete}
        body={'Are you sure you want to remove selected Image?'}
      />

      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between">
          <Flex gap="4">
            <div className="text-nowrap h4">Gallery</div>

            <AdvanceTableSearchBox
              className="table-input-search-width w-75"
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              showSearchIcon={false}
            />
          </Flex>

          <Flex gap="4" alignItems="center">
            <FontAwesomeIcon
              icon={faList}
              role="button"
              className={classNames({
                'text-primary': layout === 'list'
              })}
              onClick={() => {
                setLayout('list');
              }}
            />
            <FontAwesomeIcon
              icon={faBorderAll}
              role="button"
              className={classNames({
                'text-primary': layout === 'grid'
              })}
              onClick={() => {
                setLayout('grid');
              }}
            />

            {id && (
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  setAddImageShow(true);
                }}
              >
                Add Image
              </Button>
            )}
          </Flex>
        </Card.Header>
        <Card.Body
          className={classNames('ps-3', {
            'p-0 overflow-hidden': isList,
            'pb-0': isGrid
          })}
        >
          <Row
            className={classNames({
              'g-0': isList
            })}
          >
            {paginatedItems.map((image, index) =>
              layout === 'list' ? (
                <ImageList
                  key={image.id}
                  ImageObj={image}
                  index={index}
                  setSelectedId={setSelectedId}
                  setShowDeleteImageModal={setShowDeleteImageModal}
                />
              ) : (
                <ImageGrid
                  key={image.id}
                  ImageObj={image}
                  index={index}
                  setSelectedId={setSelectedId}
                  setShowDeleteImageModal={setShowDeleteImageModal}
                />
              )
            )}
          </Row>
          <hr />
          <div className="mb-3">
            <AdvanceTablePagination
              pageIndex={pageOffset}
              pageCount={totalPages}
              gotoPage={page => setPageOffset(page)}
              canNextPage={pageOffset < totalPages - 1}
              nextPage={handleNext}
              canPreviousPage={pageOffset !== 0}
              previousPage={handlePrev}
            />
          </div>
        </Card.Body>
      </Card>
      <AddImageModal
        show={addImageShow}
        onSubmit={onSubmit}
        onHide={() => {
          setAddImageShow(false);
        }}
        loading={uploadLoading}
        title={'Add Photo'}
      />
    </>
  );
}

export default Gellery;
